import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Filter } from 'lucide-react';
import Table from '../common/Table';
import StatusIndicator, { getStockLevel } from '../common/StatusIndicator';
import { useApi } from '../../hooks/useApi';
import { getStock } from '../../api/client';

const CATEGORIES = [
  'ALL', 'AIRCRAFT', 'ATTACHMENTS', 'BOATS', 'BUILDINGSUPPLIES', 'CLOTHING',
  'CLOTHING_ACCESSOIRES', 'CONSUMABLES', 'COMPONENTS', 'EVENT', 'EXCHANGE',
  'FISHING', 'MEDICALS', 'SPECIAL', 'VEHICLE_PARTS', 'VEHICLES', 'WEAPONS',
  'ARTEFACTS', 'MUTANTS',
];

const ZONES = [
  'ALL', 'RASMUSTAV', 'Clear_Sky', 'SIDOROVICH', 'Bradislav',
  'Wirpmenistan', 'Briggosia', 'GREENMOUNTAIN',
];

const columns = [
  {
    key: 'classname',
    label: 'Item',
    render: (v) => <span className="text-text-primary font-medium text-xs">{v}</span>,
  },
  {
    key: 'category',
    label: 'Category',
    render: (v) => (
      <span className="text-text-secondary text-[11px] font-mono px-1.5 py-0.5 bg-terminal-bg rounded">
        {v}
      </span>
    ),
  },
  {
    key: 'zone',
    label: 'Zone',
    render: (v) => <span className="text-text-secondary text-xs">{v}</span>,
  },
  {
    key: 'quantity',
    label: 'Stock',
    mono: true,
    align: 'right',
    render: (v) => <span className="font-mono text-text-primary text-xs">{(v || 0).toLocaleString()}</span>,
  },
  {
    key: 'buyPrice',
    label: 'Buy',
    mono: true,
    align: 'right',
    render: (v, row) => (
      <span className="font-mono text-accent-green text-xs">
        ₽{(v || row.buy_price || 0).toLocaleString()}
      </span>
    ),
  },
  {
    key: 'sellPrice',
    label: 'Sell',
    mono: true,
    align: 'right',
    render: (v, row) => (
      <span className="font-mono text-accent-red text-xs">
        ₽{(v || row.sell_price || 0).toLocaleString()}
      </span>
    ),
  },
  {
    key: 'stockLevel',
    label: 'Status',
    align: 'right',
    sortable: false,
    render: (_, row) => {
      const pct = row.percent ?? (row.quantity && (row.maxQuantity || row.max_quantity)
        ? (row.quantity / (row.maxQuantity || row.max_quantity)) * 100
        : 50);
      return <StatusIndicator type="stock" value={pct} />;
    },
  },
];

export default function ItemBrowser() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [zone, setZone] = useState('ALL');

  const apiParams = useMemo(() => {
    const params = {};
    if (category !== 'ALL') params.category = category;
    if (zone !== 'ALL') params.zone = zone;
    if (search) params.search = search;
    return params;
  }, [category, zone, search]);

  const { data, loading } = useApi(getStock, apiParams);

  const items = useMemo(() => {
    if (!data) return [];
    const arr = Array.isArray(data) ? data : data.items || data.data || [];
    return arr;
  }, [data]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Package className="w-5 h-5 text-accent-green" />
          Item Browser
        </h1>
        <span className="text-xs text-text-muted font-mono">
          {items.length.toLocaleString()} items
        </span>
      </div>

      {/* Filters */}
      <div className="glass-card p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items by classname..."
            className="w-full bg-terminal-bg border border-terminal-border rounded pl-8 pr-3 py-2 text-xs text-text-primary font-mono placeholder:text-text-muted focus:outline-none focus:border-accent-green/50"
          />
        </div>

        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5 text-text-muted" />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-terminal-bg border border-terminal-border rounded px-2 py-2 text-xs text-text-secondary font-mono focus:outline-none focus:border-accent-green/50"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <select
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          className="bg-terminal-bg border border-terminal-border rounded px-2 py-2 text-xs text-text-secondary font-mono focus:outline-none focus:border-accent-green/50"
        >
          {ZONES.map((z) => (
            <option key={z} value={z}>{z}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <Table
          columns={columns}
          data={items}
          loading={loading}
          pageSize={20}
          onRowClick={(row) => row.classname && navigate(`/items/${row.classname}`)}
          emptyMessage="No items found matching your filters"
        />
      </div>
    </div>
  );
}
